import { useState, useCallback } from 'react'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Loader2,
  PlayCircle,
  BookOpen,
  FolderOpen,
  Plus,
  Save,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  ChevronLeft as ChevronLeftIcon,
  Trash2,
  ChevronDown as ChevronDownIcon,
} from 'lucide-react'
import { adminApi, curriculumBuilderApi, type AdminContent } from '@/api/admin'

interface LessonItem {
  id: number
  title: string
  qid?: string | null
}

interface ModuleLessonEditorProps {
  parentId: number
  parentTitle: string
  parentType: string
  lessons: LessonItem[]
  curriculumId: number
  onUpdated: () => void
  onBack: () => void
}

const PARENT_TYPE_LABELS: Record<string, string> = {
  curriculum: '커리큘럼',
  course: '코스',
  module: '모듈',
}

export function ModuleLessonEditor({ parentId, parentTitle, parentType, lessons, curriculumId, onUpdated, onBack }: ModuleLessonEditorProps) {
  const queryClient = useQueryClient()

  // ── Local state (not saved until user clicks 저장) ─────────────
  const [localLessons, setLocalLessons] = useState<LessonItem[]>(lessons)
  const [addedIds, setAddedIds] = useState<Set<number>>(new Set())
  const [removedIds, setRemovedIds] = useState<Set<number>>(new Set())
  const [orderChanged, setOrderChanged] = useState(false)

  // Sync when props change (after save)
  const [prevLessonsKey, setPrevLessonsKey] = useState(() => JSON.stringify(lessons))
  const currentKey = JSON.stringify(lessons)
  if (currentKey !== prevLessonsKey) {
    setPrevLessonsKey(currentKey)
    setLocalLessons(lessons)
    setAddedIds(new Set())
    setRemovedIds(new Set())
    setOrderChanged(false)
  }

  const hasChanges = addedIds.size > 0 || removedIds.size > 0 || orderChanged

  // ── Save mutation ──────────────────────────────────────────────
  const [isSaving, setIsSaving] = useState(false)

  const handleSave = useCallback(async () => {
    setIsSaving(true)
    try {
      // 1. Remove lessons
      for (const id of removedIds) {
        await curriculumBuilderApi.removeChildFromParent(parentId, id)
      }
      // 2. Add lessons
      if (addedIds.size > 0) {
        await curriculumBuilderApi.addLessonsToParent(parentId, Array.from(addedIds))
      }
      // 3. Reorder (always send current order to ensure consistency)
      if (localLessons.length > 0) {
        const orders = localLessons.map((l, i) => ({ contentId: l.id, sequenceOrder: i + 1 }))
        await curriculumBuilderApi.reorderChildren(parentId, orders)
      }

      // Reset state
      setAddedIds(new Set())
      setRemovedIds(new Set())
      setOrderChanged(false)

      queryClient.invalidateQueries({ queryKey: ['curriculum-builder', 'hierarchy', curriculumId] })
      onUpdated()
    } finally {
      setIsSaving(false)
    }
  }, [parentId, curriculumId, localLessons, addedIds, removedIds, queryClient, onUpdated])

  // ── Local operations (no API call) ─────────────────────────────
  const moveLesson = (index: number, direction: -1 | 1) => {
    const newIndex = index + direction
    if (newIndex < 0 || newIndex >= localLessons.length) return
    const updated = [...localLessons]
    const [moved] = updated.splice(index, 1)
    updated.splice(newIndex, 0, moved)
    setLocalLessons(updated)
    setOrderChanged(true)
  }

  const handleRemove = (lessonId: number) => {
    setLocalLessons((prev) => prev.filter((l) => l.id !== lessonId))
    if (addedIds.has(lessonId)) {
      // Was just added locally, just remove from addedIds
      setAddedIds((prev) => { const next = new Set(prev); next.delete(lessonId); return next })
    } else {
      // Was an original lesson, mark for removal
      setRemovedIds((prev) => new Set([...prev, lessonId]))
    }
  }

  const handleAddLesson = (lesson: LessonItem) => {
    if (existingIds.has(lesson.id)) return
    setLocalLessons((prev) => [...prev, lesson])
    if (removedIds.has(lesson.id)) {
      // Was removed then re-added, cancel removal
      setRemovedIds((prev) => { const next = new Set(prev); next.delete(lesson.id); return next })
    } else {
      setAddedIds((prev) => new Set([...prev, lesson.id]))
    }
  }

  const handleAddModuleLessons = (moduleLessons: LessonItem[]) => {
    for (const lesson of moduleLessons) {
      handleAddLesson(lesson)
    }
  }

  const existingIds = new Set(localLessons.map((l) => l.id))

  return (
    <div className="flex h-full w-full">
      {/* Left: Curriculum tree for picking lessons */}
      <div className="w-1/2 border-r border-slate-200 bg-white flex flex-col min-w-0">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 shrink-0">
          <button
            onClick={onBack}
            className="p-1 text-slate-400 hover:text-slate-600 rounded"
            title="돌아가기"
          >
            <ChevronLeftIcon className="w-4 h-4" />
          </button>
          <div>
            <h3 className="text-sm font-semibold text-slate-700">콘텐츠 트리</h3>
            <p className="text-xs text-slate-400">레슨 또는 모듈의 + 버튼을 클릭하여 추가</p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          <LessonPickerTree
            existingIds={existingIds}
            onAddLesson={handleAddLesson}
            onAddModuleLessons={handleAddModuleLessons}
          />
        </div>
      </div>

      {/* Right: Selected lessons */}
      <div className="w-1/2 bg-slate-50 flex flex-col min-w-0">
        <div className="px-4 py-3 border-b border-slate-200 bg-white flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-sm font-semibold text-slate-700">
              선택된 레슨 ({localLessons.length}개)
            </h3>
            <p className="text-xs text-slate-400">{PARENT_TYPE_LABELS[parentType] || parentType}: <ContentMetadataText text={parentTitle} /></p>
          </div>
          <button
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            저장
            {hasChanges && (
              <span className="w-2 h-2 rounded-full bg-amber-300" />
            )}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {localLessons.length === 0 ? (
            <div className="text-center py-16 text-sm text-slate-400">
              왼쪽 트리에서 레슨을 추가하세요
            </div>
          ) : (
            <div className="divide-y divide-slate-100 bg-white">
              {localLessons.map((lesson, index) => {
                const isNew = addedIds.has(lesson.id)
                return (
                  <div
                    key={lesson.id}
                    className={`flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors ${isNew ? 'bg-blue-50/50' : ''}`}
                  >
                    <span className="text-xs text-slate-400 w-6 text-right shrink-0">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <PlayCircle className="w-4 h-4 text-green-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-slate-900 truncate block"><ContentMetadataText text={lesson.title} /></span>
                      {lesson.qid && <span className="text-xs text-slate-400">{lesson.qid}</span>}
                    </div>
                    {isNew && <span className="text-[10px] text-blue-500 font-medium shrink-0">NEW</span>}
                    <div className="flex items-center gap-0.5 shrink-0">
                      <button
                        onClick={() => moveLesson(index, -1)}
                        disabled={index === 0}
                        className="p-1 text-slate-300 hover:text-slate-600 disabled:opacity-30 rounded"
                      >
                        <ChevronUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => moveLesson(index, 1)}
                        disabled={index === localLessons.length - 1}
                        className="p-1 text-slate-300 hover:text-slate-600 disabled:opacity-30 rounded"
                      >
                        <ChevronDown className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <button
                      onClick={() => handleRemove(lesson.id)}
                      className="p-1 text-slate-300 hover:text-red-500 rounded shrink-0"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Lesson Picker Tree
// ============================================================================

function LessonPickerTree({
  existingIds,
  onAddLesson,
  onAddModuleLessons,
}: {
  existingIds: Set<number>
  onAddLesson: (lesson: LessonItem) => void
  onAddModuleLessons: (lessons: LessonItem[]) => void
}) {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())

  const { data: curricula, isLoading } = useQuery({
    queryKey: ['admin-contents-tree', 'curriculum'],
    queryFn: () => adminApi.getContentsByLevel('curriculum'),
  })

  const toggleExpand = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    )
  }

  return (
    <div className="text-sm">
      {curricula?.map((curr: AdminContent) => (
        <PickerNode
          key={curr.id}
          content={curr}
          level={0}
          expandedIds={expandedIds}
          onToggle={toggleExpand}
          existingIds={existingIds}
          onAddLesson={onAddLesson}
          onAddModuleLessons={onAddModuleLessons}
        />
      ))}
    </div>
  )
}

function PickerNode({
  content,
  level,
  expandedIds,
  onToggle,
  existingIds,
  onAddLesson,
  onAddModuleLessons,
}: {
  content: AdminContent
  level: number
  expandedIds: Set<number>
  onToggle: (id: number) => void
  existingIds: Set<number>
  onAddLesson: (lesson: LessonItem) => void
  onAddModuleLessons: (lessons: LessonItem[]) => void
}) {
  const isExpanded = expandedIds.has(content.id)
  const isLesson = content.hierarchyLevel === 'lesson'
  const isModule = content.hierarchyLevel === 'module'
  const canExpand = !isLesson

  const { data: children } = useQuery({
    queryKey: ['admin-content-children', content.id],
    queryFn: () => adminApi.getChildContents(content.id),
    enabled: isExpanded && canExpand,
  })

  const Icon = isLesson ? PlayCircle : isModule ? FolderOpen : BookOpen
  const isAlreadyAdded = isLesson && existingIds.has(content.id)

  const handleAddModule = async () => {
    if (!isModule) return
    const lessonData = children || await adminApi.getChildContents(content.id)
    const lessons: LessonItem[] = lessonData
      .filter((c: AdminContent) => c.hierarchyLevel === 'lesson')
      .filter((c: AdminContent) => !existingIds.has(c.id))
      .map((c: AdminContent) => ({ id: c.id, title: c.title, qid: c.qid }))
    if (lessons.length > 0) {
      onAddModuleLessons(lessons)
    }
  }

  return (
    <div>
      <div
        className={`flex items-center gap-1.5 py-1.5 px-2 rounded cursor-pointer transition-colors ${
          isAlreadyAdded ? 'opacity-40' : 'hover:bg-slate-50'
        }`}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={() => canExpand ? onToggle(content.id) : undefined}
      >
        {canExpand ? (
          isExpanded
            ? <ChevronDownIcon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            : <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        ) : (
          <span className="w-3.5 shrink-0" />
        )}
        <Icon className={`w-3.5 h-3.5 shrink-0 ${isLesson ? 'text-green-400' : 'text-slate-400'}`} />
        <span className="truncate text-slate-700 flex-1"><ContentMetadataText text={content.title} /></span>

        {/* Module: "+" adds all lessons */}
        {isModule && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              handleAddModule()
            }}
            className="p-0.5 text-blue-500 hover:bg-blue-50 rounded shrink-0"
            title="모듈 내 레슨 전체 추가"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        )}

        {/* Lesson: "+" adds single lesson */}
        {isLesson && !isAlreadyAdded && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onAddLesson({ id: content.id, title: content.title, qid: content.qid })
            }}
            className="p-0.5 text-blue-500 hover:bg-blue-50 rounded shrink-0"
            title="레슨 추가"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        )}

        {isAlreadyAdded && (
          <span className="text-[10px] text-slate-400 shrink-0">추가됨</span>
        )}
      </div>
      {isExpanded && children?.map((child: AdminContent) => (
        <PickerNode
          key={child.id}
          content={child}
          level={level + 1}
          expandedIds={expandedIds}
          onToggle={onToggle}
          existingIds={existingIds}
          onAddLesson={onAddLesson}
          onAddModuleLessons={onAddModuleLessons}
        />
      ))}
    </div>
  )
}
